import type { SmartScheduleEvent } from '../smartSchedule/types';
import { toSmartScheduleKst } from '../smartSchedule/date';
import { resolveExplicitGroupSessionId, resolveHistoryMemberId } from '../../lib/scheduleHistory';
import {
    hasExplicitScheduleAppAccountStatus,
    isWithdrawnScheduleReadOnly,
} from '../../lib/scheduleMemberLink';
import {
    QuickMoveBlockReason,
    QuickMoveEligibility,
    QuickMoveMemberAccountState,
    QuickMoveSource,
} from './types';

const GROUP_CLASS_TYPES = new Set(['GROUP', 'PRIVATE_1ON2', 'PRIVATE_1ON3']);

const asString = (value: unknown): string => (value === undefined || value === null ? '' : String(value).trim());

const BLOCK_MESSAGES: Record<QuickMoveBlockReason, string> = {
    READ_ONLY_SESSION: '열람 모드에서는 일정을 바꿀 수 없어요.',
    UNSUPPORTED_KIND: 'PT와 일반일정만 시간·날짜를 바꿀 수 있어요.',
    GROUP_LINKED: '그룹수업에 연결된 일정은 여기서 바꿀 수 없어요.',
    GROUP_SOURCE_FAILED: '그룹수업 정보를 불러오지 못해 지금은 바꿀 수 없어요. 새로고침 후 다시 시도해주세요.',
    GENERAL_NAME_MASKED: '가려진 개인 일정은 원래 제목을 알 수 없어 바꿀 수 없어요.',
    GENERAL_NAME_MASK_UNKNOWN: '일정 제목 공개 여부를 확인할 수 없어 바꿀 수 없어요.',
    MEMBER_WITHDRAWN: '탈퇴 회원의 일정은 조회만 가능합니다.',
    MEMBER_STATUS_PENDING: '회원 상태를 확인하고 있습니다.',
    MEMBER_STATUS_UNAVAILABLE: '회원 상태를 확인할 수 없어 변경할 수 없어요.',
    MISSING_SOURCE: '일정 정보가 부족해 바꿀 수 없어요.',
    AMBIGUOUS_SOURCE: '일정을 하나로 특정할 수 없어 바꿀 수 없어요.',
};

const block = (reason: QuickMoveBlockReason, pending = false): QuickMoveEligibility => ({
    allowed: false,
    reason,
    message: BLOCK_MESSAGES[reason],
    pending,
});

const rawIdOf = (raw: any): string => asString(raw?._id || raw?.id || raw?.scheduleId);

/** rawItems 가 정확히 한 원본을 가리킬 때만 그 원본을 돌려준다. */
const singleRawItem = (event: SmartScheduleEvent): { raw?: any; ambiguous: boolean } => {
    const rawItems = Array.isArray(event.rawItems) ? event.rawItems.filter(Boolean) : [];
    if (rawItems.length === 0) return { ambiguous: false };
    const ids = new Set(rawItems.map(rawIdOf));
    if (ids.size !== 1 || ids.has('')) return { ambiguous: true };
    return { raw: rawItems[0], ambiguous: false };
};

/**
 * 회원 상태를 따로 조회해야 하는가 — 상세 모달(`ScheduleDetailModal.tsx`)과 같은 규칙.
 * 일정 응답에 3상태/탈퇴 시각/isReadOnly 가 명시돼 있으면 조회하지 않는다.
 * 조회가 필요하면 회원 id 를, 아니면 빈 문자열을 돌려준다(PT 가 아니면 항상 빈 문자열).
 */
export const getQuickMoveMemberLookupId = (event: SmartScheduleEvent | null | undefined): string => {
    if (!event || event.kind !== 'private') return '';
    const { raw } = singleRawItem(event);
    if (!raw) return '';
    const memberId = asString(raw.memberId || resolveHistoryMemberId(raw));
    if (!memberId) return '';
    return hasExplicitScheduleAppAccountStatus(raw) ? '' : memberId;
};

export interface ResolveQuickMoveEligibilityInput {
    event: SmartScheduleEvent | null | undefined;
    /** useAccessMode().isReadOnly — 열람 세션(accessMode=view / center_viewer). */
    isReadOnly: boolean;
    /** Smart 그룹수업 소스(useGroupSessions) 조회가 실패한 상태인가. */
    groupSourceFailed: boolean;
    /**
     * getQuickMoveMemberLookupId 가 id 를 돌려줬을 때의 회원 상세 조회 상태.
     * 조회가 필요한데 넘기지 않으면 '확인 중'으로 본다(fail-closed).
     */
    memberAccount?: QuickMoveMemberAccountState | null;
}

/**
 * 빠른 변경 가능 여부. 판정 근거는 Smart 정규화 이벤트의 **rawItems 원본 값**이다.
 *
 * 불가: 열람 세션, PT/일반일정 외 종류, 그룹 연결 PT, 그룹 소스 조회 실패,
 * 가려진 일반일정(nameMasked !== false — 필드가 없는 구서버 포함), 탈퇴 회원·회원 상태 확인 실패/중,
 * 원본이 없거나 하나로 특정되지 않는 경우.
 */
export const resolveQuickMoveEligibility = ({
    event,
    isReadOnly,
    groupSourceFailed,
    memberAccount,
}: ResolveQuickMoveEligibilityInput): QuickMoveEligibility => {
    if (isReadOnly) return block('READ_ONLY_SESSION');
    if (!event) return block('MISSING_SOURCE');
    if (event.kind !== 'private' && event.kind !== 'general') return block('UNSUPPORTED_KIND');

    // 그룹 소스가 없으면 1:2 그룹 행이 PT 로 잘못 보일 수 있다 — 어떤 일정이든 막는다.
    if (groupSourceFailed) return block('GROUP_SOURCE_FAILED');

    const { raw, ambiguous } = singleRawItem(event);
    if (ambiguous) return block('AMBIGUOUS_SOURCE');
    if (!raw) return block('MISSING_SOURCE');

    const scheduleId = rawIdOf(raw);
    const coachId = asString(raw.coachId || event.coachId);
    const start = toSmartScheduleKst(raw.startTime);
    const end = toSmartScheduleKst(raw.endTime);
    if (!scheduleId || !coachId || !raw.startTime || !raw.endTime || !start.isValid() || !end.isValid()) {
        return block('MISSING_SOURCE');
    }
    const rawType = asString(raw.type).toLowerCase();

    if (event.kind === 'general') {
        if (rawType !== 'onetimeschedule') return block('UNSUPPORTED_KIND');
        if (raw.nameMasked === true) return block('GENERAL_NAME_MASKED');
        if (raw.nameMasked !== false) return block('GENERAL_NAME_MASK_UNKNOWN');
        const generalName = typeof raw.name === 'string' ? raw.name : '';
        if (!generalName.trim()) return block('MISSING_SOURCE');
        return {
            allowed: true,
            source: buildSource(event, raw, {
                kind: 'general',
                scheduleId,
                coachId,
                memberId: '',
                memberName: '',
                title: generalName,
                generalName,
            }),
        };
    }

    // PT: v4 범위 조회가 확정 PT 행에 붙이는 type='schedule' 만 받는다(신청·기타 행 오분류 방어).
    if (rawType !== 'schedule') return block('UNSUPPORTED_KIND');
    if (GROUP_CLASS_TYPES.has(asString(raw.classType).toUpperCase()) || resolveExplicitGroupSessionId(raw)) {
        return block('GROUP_LINKED');
    }

    const memberId = asString(raw.memberId || resolveHistoryMemberId(raw));
    if (!memberId) return block('MISSING_SOURCE');

    if (isWithdrawnScheduleReadOnly(raw)) return block('MEMBER_WITHDRAWN');
    if (!hasExplicitScheduleAppAccountStatus(raw)) {
        if (!memberAccount || memberAccount.isLoading) return block('MEMBER_STATUS_PENDING', true);
        if (memberAccount.isError) return block('MEMBER_STATUS_UNAVAILABLE');
        if (isWithdrawnScheduleReadOnly(memberAccount.centerMember)) return block('MEMBER_WITHDRAWN');
    }

    const memberName = asString(raw.alias || raw.memberName || event.members?.[0]?.name || event.title);
    return {
        allowed: true,
        source: buildSource(event, raw, {
            kind: 'private',
            scheduleId,
            coachId,
            memberId,
            memberName,
            title: memberName,
            generalName: '',
        }),
    };
};

const buildSource = (
    event: SmartScheduleEvent,
    raw: any,
    fields: Pick<QuickMoveSource, 'kind' | 'scheduleId' | 'coachId' | 'memberId' | 'memberName' | 'title' | 'generalName'>
): QuickMoveSource => {
    const attendanceStatusRaw =
        typeof raw.attendanceStatus === 'string' && raw.attendanceStatus.trim() !== ''
            ? raw.attendanceStatus
            : undefined;
    return {
        ...fields,
        coachName: asString(raw.coachName || event.coachName),
        startTime: toSmartScheduleKst(raw.startTime).toISOString(),
        endTime: toSmartScheduleKst(raw.endTime).toISOString(),
        attendanceStatusRaw,
        raw,
    };
};
